'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const PhoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
  </svg>
);

const ctaServices = [
  { label: 'HVAC & AC', tel: 'tel:13213614842' },
  { label: 'Locksmith', tel: 'tel:+15551002002' },
  { label: 'Roofing', tel: 'tel:+15551003003' },
  { label: 'Plumbing', tel: 'tel:13234876798' },
];

export default function CTASection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('[data-animate]');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 px-4 md:px-6 bg-ink" ref={sectionRef}>
      <div className="max-w-5xl mx-auto text-center">
        <div
          data-animate
          style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1) 0s, transform 0.7s cubic-bezier(0.16,1,0.3,1) 0s' }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-paper/40 mb-6">Ready to Get Started?</p>
        </div>

        <div
          data-animate
          style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1) 0.1s, transform 0.7s cubic-bezier(0.16,1,0.3,1) 0.1s' }}
        >
          <h2
            className="font-sans font-extrabold text-paper leading-none mb-6"
            style={{ fontSize: 'clamp(2.6rem, 8vw, 6.5rem)', letterSpacing: '-0.04em' }}
          >
            One Call.<br />
            <span className="font-serif italic font-normal text-paper/60">Problem Solved.</span>
          </h2>
        </div>

        <div
          data-animate
          style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1) 0.2s, transform 0.7s cubic-bezier(0.16,1,0.3,1) 0.2s' }}
        >
          <p className="text-paper/50 text-lg max-w-xl mx-auto mb-14 leading-relaxed">
            Pick your service below and call the dedicated specialist directly. No hold music. No forms. Just fast, professional service.
          </p>
        </div>

        <div
          data-animate
          style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1) 0.3s, transform 0.7s cubic-bezier(0.16,1,0.3,1) 0.3s' }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10 max-w-3xl mx-auto">
            {ctaServices.map((s) => (
              <Link
                key={s.label}
                href={s.tel}
                className="flex flex-col items-center gap-2 border border-paper/10 rounded-2xl px-4 py-5 text-paper/70 hover:text-paper hover:border-paper/30 hover:bg-paper/5 transition-all duration-300 font-semibold text-sm group"
              >
                <PhoneIcon />
                {s.label}
              </Link>
            ))}
          </div>
        </div>

        <div
          data-animate
          style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1) 0.4s, transform 0.7s cubic-bezier(0.16,1,0.3,1) 0.4s' }}
        >
          <Link
            href="tel:+15550000000"
            className="inline-flex items-center gap-3 bg-paper text-ink px-10 py-5 rounded-full text-base font-bold hover:bg-paper-warm transition-all duration-300 shadow-[0_8px_30px_rgba(250,250,250,0.1)] hover:shadow-[0_12px_40px_rgba(250,250,250,0.15)] hover:scale-105 active:scale-95"
          >
            <PhoneIcon />
            Not Sure? Call Us First
          </Link>
        </div>

        <div
          data-animate
          style={{ opacity: 0, transform: 'translateY(20px)', transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1) 0.5s, transform 0.7s cubic-bezier(0.16,1,0.3,1) 0.5s' }}
        >
          <p className="mt-8 text-paper/25 text-xs uppercase tracking-widest font-bold">
            Licensed &middot; Insured &middot; Background-Checked Technicians
          </p>
        </div>
      </div>
    </section>
  );
}
